import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../../api/client';
import {
  PRIMARY, TEXT_DARK, TEXT_MUTED, TEXT_SECONDARY,
  FONT_HEADING, BG_CARD, BORDER_LIGHT, BG_PAGE, ZONE_COLORS,
} from './designTokens';

interface SeatInfo {
  seatNumber: number;
  status: string;
  guestId: string | null;
  guestName?: string;
  guestTicketNumber?: string | null;
  guestTier?: string | null;
  guestMeal?: string | null;
  guestChapter?: string | null;
  guestFamily?: string | null;
  guestVip?: boolean;
}

interface TableInfo {
  id: string;
  tableNumber: number;
  tableName: string;
  zone: { name: string; colorCode: string };
  capacity: number;
  status: string;
  seats: SeatInfo[];
  occupancy: number;
}

type SortField = 'tableName' | 'zone';

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    gap: '1rem',
    height: 'calc(100vh - 220px)',
    minHeight: '400px',
  },
  leftPane: {
    flex: '0 0 340px',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    overflow: 'hidden',
  },
  leftHeader: {
    padding: '0.75rem 1rem',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    backgroundColor: BG_PAGE,
  },
  leftTitle: {
    fontSize: '1rem',
    fontFamily: FONT_HEADING,
    fontWeight: 700,
    color: TEXT_DARK,
    margin: 0,
  },
  sortBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginTop: '0.5rem',
  },
  sortLabel: {
    fontSize: '0.75rem',
    color: TEXT_MUTED,
    fontWeight: 500,
  },
  sortBtn: {
    fontSize: '0.72rem',
    padding: '0.2rem 0.5rem',
    borderRadius: '3px',
    border: `1px solid ${BORDER_LIGHT}`,
    backgroundColor: '#fff',
    cursor: 'pointer',
    fontWeight: 500,
    color: TEXT_SECONDARY,
    transition: 'all 0.15s',
  },
  sortBtnActive: {
    fontSize: '0.72rem',
    padding: '0.2rem 0.5rem',
    borderRadius: '3px',
    border: `1px solid ${PRIMARY}`,
    backgroundColor: PRIMARY,
    cursor: 'pointer',
    fontWeight: 600,
    color: '#fff',
    transition: 'all 0.15s',
  },
  selectAllBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.4rem 1rem',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    backgroundColor: '#fafafa',
  },
  tableList: {
    flex: 1,
    overflowY: 'auto',
    padding: '0.5rem',
  },
  tableRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 0.6rem',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'background-color 0.1s',
    marginBottom: '2px',
  },
  tableRowSelected: {
    backgroundColor: '#e8f5e9',
  },
  checkbox: {
    width: '16px',
    height: '16px',
    cursor: 'pointer',
    accentColor: PRIMARY,
  },
  tableRowInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  tableRowName: {
    fontSize: '0.82rem',
    fontWeight: 600,
    color: TEXT_DARK,
  },
  tableRowMeta: {
    fontSize: '0.7rem',
    color: TEXT_MUTED,
  },
  zoneBadge: {
    fontSize: '0.65rem',
    fontWeight: 600,
    padding: '0.1rem 0.35rem',
    borderRadius: '3px',
    color: '#fff',
    whiteSpace: 'nowrap',
  },
  rightPane: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    overflow: 'hidden',
  },
  rightHeader: {
    padding: '0.75rem 1rem',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    backgroundColor: BG_PAGE,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  rightTitle: {
    fontSize: '1rem',
    fontFamily: FONT_HEADING,
    fontWeight: 700,
    color: TEXT_DARK,
    margin: 0,
  },
  printBtn: {
    padding: '0.45rem 1rem',
    fontSize: '0.82rem',
    fontWeight: 600,
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    transition: 'opacity 0.15s',
  },
  printBtnDisabled: {
    padding: '0.45rem 1rem',
    fontSize: '0.82rem',
    fontWeight: 600,
    backgroundColor: '#ccc',
    color: '#888',
    border: 'none',
    borderRadius: '5px',
    cursor: 'not-allowed',
  },
  previewArea: {
    flex: 1,
    overflowY: 'auto',
    padding: '1rem',
  },
  previewEmpty: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: TEXT_MUTED,
    fontSize: '0.9rem',
    fontStyle: 'italic',
  },
  previewTable: {
    marginBottom: '1.25rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    overflow: 'hidden',
  },
  previewTableHeader: {
    padding: '0.5rem 0.75rem',
    backgroundColor: BG_PAGE,
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  previewTableTitle: {
    fontSize: '0.85rem',
    fontWeight: 700,
    color: TEXT_DARK,
  },
  previewTableMeta: {
    fontSize: '0.7rem',
    color: TEXT_MUTED,
  },
  loading: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: TEXT_SECONDARY,
  },
  error: {
    color: '#b71c1c',
    backgroundColor: '#fdecea',
    padding: '0.75rem 1rem',
    borderRadius: '4px',
    fontSize: '0.85rem',
  },
};

export function PrintTablesReport() {
  const [tables, setTables] = useState<TableInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<SortField>('tableName');
  const [patronTicketNumbers, setPatronTicketNumbers] = useState<Set<string>>(new Set());

  const fetchTables = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [data, ticketsData] = await Promise.all([
        apiClient<any[]>('/table-management/tables'),
        apiClient<any[]>('/legacy-ball/tickets'),
      ]);

      // Build set of patron ticket numbers (formatted as TKT-XXX)
      const patronTkts = new Set<string>();
      if (Array.isArray(ticketsData)) {
        ticketsData.forEach((t: any) => {
          const tier = t.tierName || '';
          if (['Patron', 'patron', 'VIP', 'vip'].includes(tier)) {
            patronTkts.add(`TKT-${String(t.ticketNumber).padStart(3, '0')}`);
          }
        });
      }
      setPatronTicketNumbers(patronTkts);

      const mapped: TableInfo[] = data
        .map((t: any) => ({
          id: t.id,
          tableNumber: t.tableNumber,
          tableName: t.tableName,
          zone: t.zone,
          capacity: t.capacity,
          status: t.status,
          seats: (t.seats || []).map((s: any) => ({
            seatNumber: s.seatNumber,
            status: s.status,
            guestId: s.guestId,
            guestName: s.guest?.fullName || null,
            guestTicketNumber: s.guest?.ticketNumber || null,
            guestTier: s.guest?.sponsorshipLevel || null,
            guestMeal: s.guest?.dietaryPreference || null,
            guestChapter: s.guest?.chapterAffiliation || null,
            guestFamily: s.guest?.familyAssociation || null,
            guestVip: s.guest?.protocolVip || false,
          })),
          occupancy: (t.seats || []).filter(
            (s: any) => s.status === 'Assigned' || s.status === 'CheckedIn'
          ).length,
        }))
        .filter((t: TableInfo) => t.occupancy > 0);
      setTables(mapped);
    } catch {
      setError('Failed to load table data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTables();
  }, [fetchTables]);

  const sortedTables = [...tables].sort((a, b) => {
    if (sortField === 'zone') {
      const zoneCompare = a.zone.name.localeCompare(b.zone.name);
      if (zoneCompare !== 0) return zoneCompare;
      return a.tableNumber - b.tableNumber;
    }
    return a.tableNumber - b.tableNumber;
  });

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectAll = () => {
    setSelectedIds(new Set(sortedTables.map((t) => t.id)));
  };

  const deselectAll = () => {
    setSelectedIds(new Set());
  };

  const selectPatronTables = () => {
    const patronTableIds = new Set<string>();
    for (const table of sortedTables) {
      const hasPatron = table.seats.some(
        (s) => s.guestTicketNumber && patronTicketNumbers.has(s.guestTicketNumber),
      );
      if (hasPatron) {
        patronTableIds.add(table.id);
      }
    }
    setSelectedIds(patronTableIds);
  };

  const selectedTables = sortedTables.filter((t) => selectedIds.has(t.id));

  const handlePrint = () => {
    if (selectedTables.length === 0) return;

    const tableBlocks = selectedTables.map((table) => {
      const occupiedSeats = table.seats
        .filter((s) => s.status === 'Assigned' || s.status === 'CheckedIn')
        .sort((a, b) => a.seatNumber - b.seatNumber);

      const headerCells = ['Seat', 'Name', 'Ticket #', 'Tier', 'Meal', 'Chapter', 'Family', 'VIP']
        .map((h) => `<th>${h}</th>`)
        .join('');

      const rows = occupiedSeats
        .map(
          (s) =>
            `<tr><td>${s.seatNumber}</td><td>${s.guestName || ''}</td><td>${s.guestTicketNumber || ''}</td><td>${s.guestTier || ''}</td><td>${s.guestMeal || ''}</td><td>${s.guestChapter || ''}</td><td>${s.guestFamily || ''}</td><td>${s.guestVip ? 'VIP' : ''}</td></tr>`
        )
        .join('');

      return `
        <div class="table-block">
          <h2>Table ${table.tableNumber} — ${table.tableName}</h2>
          <p>${table.zone.name} Zone &bull; ${occupiedSeats.length} / ${table.capacity} guests</p>
          <table>
            <thead><tr>${headerCells}</tr></thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
      `;
    });

    // Group into pages of 2 tables each
    const pages: string[] = [];
    for (let i = 0; i < tableBlocks.length; i += 2) {
      const pageContent = tableBlocks.slice(i, i + 2).join('');
      pages.push(`<div class="page">${pageContent}</div>`);
    }

    const html = `<!DOCTYPE html><html><head>
      <title>Print Tables Report</title>
      <style>
        * { box-sizing: border-box; }
        body { font-family: sans-serif; margin: 0; padding: 20px; }
        .page { page-break-after: always; }
        .page:last-child { page-break-after: avoid; }
        .table-block { margin-bottom: 28px; }
        .table-block h2 { font-size: 16px; margin: 0 0 2px 0; }
        .table-block p { font-size: 11px; color: #666; margin: 0 0 10px 0; }
        table { width: 100%; border-collapse: collapse; }
        th { text-align: left; padding: 6px 8px; border-bottom: 2px solid #333; font-size: 11px; font-weight: 700; }
        td { padding: 5px 8px; border-bottom: 1px solid #ddd; font-size: 11px; }
        @media print {
          body { padding: 0; }
          .page { page-break-after: always; height: 100vh; display: flex; flex-direction: column; justify-content: flex-start; padding: 16px; }
          .page:last-child { page-break-after: avoid; }
        }
        .report-header { text-align: center; margin-bottom: 16px; border-bottom: 1px solid #ccc; padding-bottom: 8px; }
        .report-header h1 { font-size: 18px; margin: 0 0 4px 0; }
        .report-header p { font-size: 11px; color: #666; margin: 0; }
      </style>
    </head><body>
      <div class="report-header">
        <h1>Print Tables Report</h1>
        <p>${selectedTables.length} table(s) &bull; Printed ${new Date().toLocaleDateString()}</p>
      </div>
      ${pages.join('')}
    </body></html>`;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 300);
    }
  };

  if (loading) {
    return <div style={styles.loading}>Loading tables...</div>;
  }

  if (error) {
    return <div style={styles.error}>{error}</div>;
  }

  return (
    <div style={styles.container}>
      {/* Left Pane — Table Selection */}
      <div style={styles.leftPane}>
        <div style={styles.leftHeader}>
          <h3 style={styles.leftTitle}>Select Tables to Print</h3>
          <div style={styles.sortBar}>
            <span style={styles.sortLabel}>Sort by:</span>
            <button
              style={sortField === 'tableName' ? styles.sortBtnActive : styles.sortBtn}
              onClick={() => setSortField('tableName')}
              aria-pressed={sortField === 'tableName'}
            >
              Table #
            </button>
            <button
              style={sortField === 'zone' ? styles.sortBtnActive : styles.sortBtn}
              onClick={() => setSortField('zone')}
              aria-pressed={sortField === 'zone'}
            >
              Zone
            </button>
          </div>
        </div>

        <div style={styles.selectAllBar}>
          <span style={{ fontSize: '0.72rem', color: TEXT_MUTED }}>
            {selectedIds.size} of {sortedTables.length} selected
          </span>
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <button
              style={{ ...styles.sortBtn, fontSize: '0.68rem' }}
              onClick={selectAll}
              aria-label="Select all tables"
            >
              Select All
            </button>
            <button
              style={{ ...styles.sortBtn, fontSize: '0.68rem', backgroundColor: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca' }}
              onClick={selectPatronTables}
              aria-label="Select tables with Patron guests"
            >
              🔺 Patron Tables
            </button>
            <button
              style={{ ...styles.sortBtn, fontSize: '0.68rem' }}
              onClick={deselectAll}
              aria-label="Deselect all tables"
            >
              Clear
            </button>
          </div>
        </div>

        <div style={styles.tableList} role="listbox" aria-label="Tables with guests" aria-multiselectable="true">
          {sortedTables.map((table) => {
            const isSelected = selectedIds.has(table.id);
            return (
              <div
                key={table.id}
                style={{
                  ...styles.tableRow,
                  ...(isSelected ? styles.tableRowSelected : {}),
                }}
                onClick={() => toggleSelect(table.id)}
                role="option"
                aria-selected={isSelected}
              >
                <input
                  type="checkbox"
                  style={styles.checkbox}
                  checked={isSelected}
                  onChange={() => toggleSelect(table.id)}
                  aria-label={`Select table ${table.tableNumber}`}
                  onClick={(e) => e.stopPropagation()}
                />
                <div style={styles.tableRowInfo}>
                  <span style={styles.tableRowName}>
                    Table {table.tableNumber} — {table.tableName}
                  </span>
                  <span style={styles.tableRowMeta}>
                    {table.occupancy}/{table.capacity} guests
                  </span>
                </div>
                <span
                  style={{
                    ...styles.zoneBadge,
                    backgroundColor: ZONE_COLORS[table.zone.name] || table.zone.colorCode,
                  }}
                >
                  {table.zone.name}
                </span>
              </div>
            );
          })}
          {sortedTables.length === 0 && (
            <div style={{ padding: '1.5rem', textAlign: 'center', color: TEXT_MUTED, fontSize: '0.85rem' }}>
              No tables with guests found.
            </div>
          )}
        </div>
      </div>

      {/* Right Pane — Preview & Print */}
      <div style={styles.rightPane}>
        <div style={styles.rightHeader}>
          <h3 style={styles.rightTitle}>
            Preview ({selectedTables.length} table{selectedTables.length !== 1 ? 's' : ''})
          </h3>
          <button
            style={selectedTables.length > 0 ? styles.printBtn : styles.printBtnDisabled}
            onClick={handlePrint}
            disabled={selectedTables.length === 0}
            aria-label="Print selected tables"
          >
            🖨️ Print Tables
          </button>
        </div>

        <div style={styles.previewArea}>
          {selectedTables.length === 0 ? (
            <div style={styles.previewEmpty}>
              Select tables from the left to preview and print.
            </div>
          ) : (
            selectedTables.map((table) => {
              const occupiedSeats = table.seats
                .filter((s) => s.status === 'Assigned' || s.status === 'CheckedIn')
                .sort((a, b) => a.seatNumber - b.seatNumber);

              return (
                <div key={table.id} style={styles.previewTable}>
                  <div style={styles.previewTableHeader}>
                    <span style={styles.previewTableTitle}>
                      Table {table.tableNumber} — {table.tableName}
                    </span>
                    <span style={styles.previewTableMeta}>
                      {table.zone.name} &bull; {occupiedSeats.length}/{table.capacity}
                    </span>
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem' }}>
                    <thead>
                      <tr>
                        {['Seat', 'Name', 'Ticket', 'Tier', 'Meal', 'Chapter', 'Family', 'VIP'].map((h) => (
                          <th
                            key={h}
                            style={{
                              textAlign: 'left',
                              padding: '4px 6px',
                              borderBottom: `1px solid ${BORDER_LIGHT}`,
                              fontWeight: 600,
                              color: TEXT_MUTED,
                              fontSize: '0.68rem',
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {occupiedSeats.map((s) => (
                        <tr key={s.seatNumber}>
                          <td style={{ padding: '3px 6px' }}>{s.seatNumber}</td>
                          <td style={{ padding: '3px 6px', fontWeight: 500 }}>{s.guestName || '—'}</td>
                          <td style={{ padding: '3px 6px', fontFamily: 'monospace', fontSize: '0.66rem' }}>{s.guestTicketNumber || '—'}</td>
                          <td style={{ padding: '3px 6px' }}>{s.guestTier || '—'}</td>
                          <td style={{ padding: '3px 6px' }}>{s.guestMeal || '—'}</td>
                          <td style={{ padding: '3px 6px' }}>{s.guestChapter || '—'}</td>
                          <td style={{ padding: '3px 6px' }}>{s.guestFamily || '—'}</td>
                          <td style={{ padding: '3px 6px' }}>
                            {s.guestVip ? <span style={{ fontWeight: 700, color: '#e65100', fontSize: '0.65rem' }}>VIP</span> : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
